// Port of the Rhine opening sequence. Timing tracks are the original
// Assets/animation/boot-tracks.json; phrase outlines are
// Resources/boot-lettering-art.json. Motion and frames follow boot-motion.ts
// from RhineLabUI (MIT, Copyright (c) 2026 LBEILC).
import tracks from "../Rhine源码/Assets/animation/boot-tracks.json";
import letteringArt from "../static/native/boot-lettering-art.json";

const rad = Math.PI / 180;
const DEG = 180 / Math.PI;
const ns = "http://www.w3.org/2000/svg";

// Monotone cubic interpolation, matching boot-tracks.ts.
function track(keys, frame) {
  if (frame <= keys[0][0]) return keys[0][1];
  const last = keys.length - 1;
  if (frame >= keys[last][0]) return keys[last][1];
  const slope = (i) => (keys[i + 1][1] - keys[i][1]) / (keys[i + 1][0] - keys[i][0]);
  const tangent = (i) => {
    if (i === 0) return slope(0);
    if (i === last) return slope(last - 1);
    const a = slope(i - 1),
      b = slope(i);
    if (a * b <= 0) return 0;
    const h0 = keys[i][0] - keys[i - 1][0],
      h1 = keys[i + 1][0] - keys[i][0];
    const w0 = 2 * h1 + h0,
      w1 = h1 + 2 * h0;
    return (w0 + w1) / (w0 / a + w1 / b);
  };
  let i = 0;
  while (frame > keys[i + 1][0]) i++;
  const h = keys[i + 1][0] - keys[i][0],
    p = (frame - keys[i][0]) / h;
  return (
    (2 * p ** 3 - 3 * p ** 2 + 1) * keys[i][1] +
    (p ** 3 - 2 * p ** 2 + p) * h * tangent(i) +
    (-2 * p ** 3 + 3 * p ** 2) * keys[i + 1][1] +
    (p ** 3 - p ** 2) * h * tangent(i + 1)
  );
}
const value = (key, frame) => track(tracks[key], frame);
const brandKeys = tracks.brandX.map((x, i) => [i, x]);
function brandTrack(frame, line) {
  const local = frame - 278 - line * 2;
  return {
    x: track(brandKeys, local),
    opacity: track([[-1, 0], [0, 0.4], [1, 0.65], [2, 0.88], [3, 1]], local),
  };
}
const companyTrack = (frame) => track(tracks.highlightKeys, frame) / 410;

function scanTrack(frame) {
  return {
    radius: value("scan.radius", frame),
    whiteRadius: value("scan.whiteRadius", frame),
    outerStart: rad * value("scan.outerStart", frame),
    outerSweep: rad * value("scan.outerSweep", frame),
    whiteStart: rad * value("scan.whiteStart", frame),
    whiteSweep: rad * value("scan.whiteSweep", frame),
    innerRadius: value("scan.innerRadius", frame),
    innerStart: rad * value("scan.innerStart", frame),
    innerSweep: rad * value("scan.innerSweep", frame),
    orbit: rad * value("scan.orbit", frame),
    orbitRadius: value("scan.orbitRadius", frame),
    dotRadius: value("scan.dotRadius", frame),
    blackCap: value("scan.blackCap", frame),
    whiteCap: value("scan.whiteCap", frame),
  };
}

const drawStart = tracks["logo.draw"].map(([f, s]) => [f, s]);
const drawEnd = tracks["logo.draw"].map(([f, , e]) => [f, e]);
const orbitRows = tracks["orbit.sides"];
const orbitColumns = Array.from({ length: 9 }, (_, i) =>
  orbitRows.map((row) => [row[0], row[i + 1]]),
);
const orbitAngle = tracks["orbit.orbit"].map(([f, x, y]) => [
  f,
  Math.atan2(y - 539.5, x - 959.5),
]);
const orbitRadius = tracks["orbit.orbit"].map(([f, x, y]) => [
  f,
  Math.hypot(x - 959.5, y - 539.5),
]);

function logoBeat(frame) {
  const cut = value("logo.movingCut", frame);
  const start = frame < 420 ? track(drawStart, frame) : cut + 0.0275;
  const end = frame < 420 ? track(drawEnd, frame) : cut + 1;
  return {
    offsetX: value("logo.leftEdge", frame) - 520,
    start,
    length: Math.max(0, Math.min(1, end - start)),
    strokeWidth: value("logo.strokeWidth", frame),
    symbolScale: value("logo.symbolScale", frame),
    plusX: value("logo.plusX", frame),
    minusX: value("logo.minusX", frame),
    minusWidth: value("logo.minusWidth", frame),
    plusAngle: value("logo.plusAngle", frame),
  };
}

function orbitBeat(frame) {
  const cols = orbitColumns.map((keys) => track(keys, frame));
  const [lx, ly, rx, ry, radius, leftStart, rightStart, leftSweep, rightSweep] = cols;
  const angle = track(orbitAngle, frame),
    distance = track(orbitRadius, frame);
  const sourceFrame = Math.floor(frame + 0.00001);
  const flash = [546, 547, 549, 550].includes(sourceFrame);
  const coreRadius = flash
    ? track([[546, 42.93], [550, 42.43]], frame)
    : value("orbit.core", frame);
  return {
    sides: [
      { x: lx, y: ly, radius, start: leftStart * rad, sweep: leftSweep * rad },
      { x: rx, y: ry, radius, start: rightStart * rad, sweep: rightSweep * rad },
    ],
    sideVisible: frame >= 544,
    coreRadius,
    satellites: Array.from({ length: 6 }, (_, i) => {
      const phase = angle + (i * Math.PI) / 3;
      return {
        x: 959.5 + Math.cos(phase) * distance,
        y: 539.5 + Math.sin(phase) * distance,
        radius: track(tracks["orbit.dotGrowth"], frame - 548 - i * 3),
      };
    }),
  };
}

const typed = (text, f, start, end) =>
  text.slice(
    0,
    f < start
      ? 0
      : Math.min(text.length, 1 + Math.floor(((f - start) * (text.length - 1)) / (end - start))),
  );
const at = (f, frames) => frames.includes(f);
const accessCounts = [1, 1, 3, 4, 5, 6, 9, 11, 12, 14, 17, 18, 19, 20, 22, 23, 25, 26];
const clamp = (v) => Math.max(0, Math.min(1, v));
const smooth = (p) => p * p * (3 - 2 * p);
const progress = (t, a, b) => clamp((t - a) / (b - a));

export function bootMotion(appTime) {
  const t = appTime + 5;
  const f = Math.floor(t * 25 + 0.00001);
  const step =
    t < 9.12 ? "access" : t < 11.12 ? "logo" : t < 19.48 ? "auth" : t < 22.76 ? "scan" : "welcome";
  let auth = "";
  if (f < 363) {
    auth = typed("ID CONFIRMED", f, 282, 295);
    if (f >= 320) auth += " : " + typed("JOYCE MOORE", f, 321, 339);
  } else if (f < 421) auth = typed("REQUEST RECEIVED", f, 367, 389);
  else {
    auth = typed("START PROCESSING", f, 423, 440);
    if (f >= 449) auth += ".".repeat(Math.min(3, 1 + Math.floor((f - 449) / 4)));
    if (at(f, [479, 485, 486])) auth = "              SING...";
  }
  const frame = t * 25;
  const scan = scanTrack(frame);
  const scanOrbit = orbitBeat(frame);
  const scanGlitch = at(f, [525, 526, 528, 529]);
  const welcomeIntro = [1, 0, 0.28, 0, 1, 0, 0];
  const flashIndex = f - 569;
  const exit = smooth(progress(t, 26.56, 26.92));
  return {
    t,
    f,
    step,
    auth,
    access: "ACCESS PERMISSION REQUIRED".slice(
      0,
      f < 170 ? 0 : accessCounts[Math.min(17, f - 170)],
    ),
    accessOpacity: f >= 170 && f < 227 ? (f === 226 ? 0.25 : 1) : 0,
    logoOpacity: t >= 9.16 && t < 19.48 ? 1 : 0,
    logo: logoBeat(frame),
    logoLetters: typed("RHINE·LAB", f, 232, 255),
    authOpacity: f >= 281 && f < 487 ? 1 : 0,
    brand: [0, 1, 2].map((line) => brandTrack(frame, line)),
    poweredLetters: typed("POWERED BY RHINE LAB", f, 279, 295).length,
    scanVisible: t >= 19.48 && t < 22.76,
    scan,
    scanOrbit,
    scanRadius: scan.radius,
    ringScale: scanGlitch ? 1.94 : 1,
    ringOpacity: scanGlitch ? 0.32 : value("ringOpacity", frame),
    ringBlur: scanGlitch ? 2.2 : 0,
    scanTracking: value("scanTracking", frame),
    scanFont: 26.5,
    permissionOpacity:
      t < 21.8
        ? progress(t, 19.48, 19.88)
        : value("permissionOpacity", frame),
    ornament: t >= 21.84,
    coreRadius: scanOrbit.coreRadius,
    welcomeVisible: t >= 22.76 && t < 26.92,
    welcomePanel: flashIndex >= 0 && flashIndex < 7 ? welcomeIntro[flashIndex] : 0,
    welcomeInk: flashIndex >= 0 && flashIndex < 7 ? [0, 0, 0.2, 1, 0, 0, 0.25][flashIndex] : 1,
    companyVisible: f >= 588 && !at(f, [590, 591]),
    companyMask: at(f, [594, 595]),
    highlight: companyTrack(frame),
    databaseOpacity: f < 626 || at(f, [628, 629, 631, 634]) ? 0 : 1,
    welcomeLogo: f >= 588,
    welcomeScale: 1 - 0.46 * exit,
    welcomeOpacity: 1 - Math.pow(exit, 3),
    exitBlur: 8 * exit,
    exit,
    backgroundOpacity: t < 26.92 ? 1 : 0,
    white: smooth(progress(t, 26.16, 26.88)),
  };
}

const MARK =
  "M295 73C295 41 273 15 240 15C221 15 207 23 192 38C186 43 181 47 176 52C127 96 103 128 70 128C38 128 15 101 15 70C15 39 37 15 70 15C103 15 127 48 156 75C182 99 208 128 240 128C273 128 295 105 295 73Z";

class Lettering {
  constructor(host, key) {
    this.host = host;
    this.art = letteringArt[key];
    this.text = "";
    this.nodes = [];
    this.host.replaceChildren();
    const fragment = document.createDocumentFragment();
    this.art.letters.forEach((letter, index) => {
      const span = document.createElement("span");
      span.className = "boot-phrase-letter";
      span.style.width = letter.width + "em";
      span.dataset.index = String(index);
      if (letter.path) {
        const svg = document.createElementNS(ns, "svg");
        svg.setAttribute("class", "boot-letter-art");
        svg.setAttribute("viewBox", "0 0 1000 1000");
        svg.setAttribute("preserveAspectRatio", "none");
        svg.setAttribute("aria-hidden", "true");
        const path = document.createElementNS(ns, "path");
        path.setAttribute("d", letter.path);
        svg.append(path);
        span.append(svg);
      }
      span.hidden = true;
      this.nodes.push(span);
      fragment.append(span);
    });
    this.host.append(fragment);
  }
  setText(text) {
    if (text === this.text) return;
    this.text = text;
    const length = Math.min(text.length, this.nodes.length);
    this.nodes.forEach((node, index) => {
      node.hidden = index >= length;
    });
  }
  reset() {
    this.setText("");
  }
}

const arc = (r, start, sweep, x = 960, y = 540) => {
  const point = (a) => `${x + Math.cos(a) * r},${y + Math.sin(a) * r}`;
  if (sweep >= Math.PI * 1.999)
    return `M${point(start)}A${r},${r} 0 1 1 ${point(start + Math.PI)}A${r},${r} 0 1 1 ${point(start + Math.PI * 2)}`;
  return `M${point(start)}A${r},${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${point(start + sweep)}`;
};

const markSvg = (withText = true) => `
  <svg viewBox="0 0 310 185" aria-hidden="true">
    <path class="boot-contour" d="${MARK}" pathLength="1" fill="none" stroke="currentColor" stroke-width="26"/>
    <path class="boot-plus" d="M44 70h50M69 45v50" fill="none" stroke="currentColor" stroke-width="15"/>
    <path class="boot-minus" d="M219 70h44" fill="none" stroke="currentColor" stroke-width="15"/>
    ${withText ? '<text class="boot-letters" x="20" y="174" text-anchor="start" font-size="16" font-weight="700" letter-spacing="22">RHINE·LAB</text>' : ""}
  </svg>`;

export class BootSequence {
  constructor(root, { onDone } = {}) {
    this.root = root;
    this.onDone = onDone;
    this.playing = false;
    this.done = false;
    this.build();
    this.bind();
  }
  build() {
    this.root.hidden = false;
    this.root.innerHTML = `
      <div id="bootBackground" class="boot-background">
        <svg viewBox="0 0 1920 1080" preserveAspectRatio="none" aria-hidden="true">
          <defs><pattern id="bootGrid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M48 0H0V48" fill="none" stroke="#7c7a72" stroke-width="1"/>
          </pattern></defs>
          <rect width="1920" height="1080" fill="url(#bootGrid)"/>
        </svg>
      </div>
      <div class="boot">
        <div class="boot-logo">${markSvg()}</div>
        <div class="auth-status"><span>AUTH</span><span class="auth-phrase" data-art="identity"></span><span class="auth-phrase" data-art="request"></span><span class="auth-phrase" data-art="processing"></span><span class="auth-phrase" data-art="processingGlitch"></span><i></i></div>
        <div class="access-text"></div>
        <div class="scan">
          <svg viewBox="0 0 1920 1080" aria-hidden="true">
            <g class="scan-paths">
              <path class="scan-outer" fill="none" stroke="#aaa9a0" stroke-width="2.4"/>
              <path class="scan-white" fill="none" stroke="#f7f5f0" stroke-width="4"/>
              <path class="scan-inner" fill="none" stroke="#e7c69b" stroke-width="3"/>
              <path class="scan-inner2" fill="none" stroke="#e7c69b" stroke-width="3"/>
              <path class="scan-side" fill="none" stroke="#f7f5f0" stroke-width="3"/>
              <path class="scan-side" fill="none" stroke="#f7f5f0" stroke-width="3"/>
            </g>
            <g class="scan-orbit">
              <circle class="orbit-dot" fill="#e7c69b" r="0"/>
              <circle class="orbit-dot" fill="#e7c69b" r="0"/>
            </g>
            <g class="scan-nodes"></g>
          </svg>
          <span class="scan-label"></span>
        </div>
        <div class="welcome">
          <div class="welcome-heading"></div>
          <div class="welcome-panel"></div>
          <div class="welcome-company"><strong></strong><strong></strong></div>
          <div class="welcome-highlight"></div>
          <div class="welcome-database"></div>
          <div class="welcome-logo">${markSvg(false)}</div>
        </div>
        <div class="boot-brand">
          <div class="boot-brand-line boot-brand-heading"></div>
          <div class="boot-brand-line">SYNTHESIZE INFORMATION</div>
          <div class="boot-brand-line"><span class="boot-brand-analysis">ANALYSIS</span> <b>OS</b></div>
        </div>
        <div class="powered">POWERED BY <b>RHINE LAB</b><i></i></div>
        <button class="boot-skip" type="button">跳过开场 <span>ENTER</span></button>
        <div class="boot-white"></div>
      </div>`;
    this.el = (selector) => this.root.querySelector(selector);
    this.contour = this.el(".boot-contour");
    this.plus = this.el(".boot-plus");
    this.minus = this.el(".boot-minus");
    this.letters = this.el(".boot-letters");
    this.brandLines = Array.from(this.root.querySelectorAll(".boot-brand > .boot-brand-line"));
    this.scanPaths = Array.from(this.root.querySelectorAll(".scan-paths path"));
    this.orbitDots = Array.from(this.root.querySelectorAll(".orbit-dot"));
    this.scanNodes = this.el(".scan-nodes");
    this.satellites = Array.from({ length: 6 }, () => {
      const dot = document.createElementNS(ns, "circle");
      dot.setAttribute("fill", "#080a08");
      this.scanNodes.append(dot);
      return dot;
    });
    this.core = document.createElementNS(ns, "circle");
    this.core.setAttribute("fill", "#080a08");
    this.core.setAttribute("cx", "959.5");
    this.core.setAttribute("cy", "539.5");
    this.scanNodes.append(this.core);
    this.caps = ["#080a08", "#fff"].map((fill) => {
      const cap = document.createElementNS(ns, "circle");
      cap.setAttribute("fill", fill);
      this.scanNodes.append(cap);
      return cap;
    });
    this.access = new Lettering(this.el(".access-text"), "access");
    this.authTexts = {};
    this.root.querySelectorAll(".auth-phrase").forEach((host) => {
      const art = host.dataset.art;
      host.hidden = true;
      this.authTexts[art] = new Lettering(host, art);
    });
    this.permission = new Lettering(this.el(".scan-label"), "permission");
    this.heading = new Lettering(this.el(".welcome-heading"), "welcome");
    this.database = new Lettering(this.el(".welcome-database"), "database");
    this.brandHeading = new Lettering(this.brandLines[0], "brand");
    this.company = Array.from(this.root.querySelectorAll(".welcome-company strong")).map(
      (el) => new Lettering(el, "company"),
    );
    this.permission.setText("PERMISSION AUTHORIZED");
    this.heading.setText("WELCOME TO");
    this.database.setText("INTERNAL DATABASE");
    this.brandHeading.setText("RHINE LAB");
    this.company.forEach((lettering) => lettering.setText("RHINE LAB.LLC."));
  }
  bind() {
    this.onKey = (event) => {
      if (event.key === "Enter" || event.key === "Escape") this.finish();
    };
    this.onClick = (event) => {
      if (event.target.closest(".boot-skip")) this.finish();
    };
    addEventListener("keydown", this.onKey);
    this.root.addEventListener("click", this.onClick);
  }
  applyAuth(text, f) {
    const key =
      f < 363 ? "identity" : f < 421 ? "request" : text === "              SING..." ? "processingGlitch" : "processing";
    for (const [name, lettering] of Object.entries(this.authTexts)) {
      lettering.host.hidden = name !== key;
      if (name === key) lettering.setText(text);
    }
  }
  update(appTime) {
    const s = bootMotion(appTime);
    const t = s.t;
    this.root.dataset.bootFrame = String(s.f);
    this.access.setText(s.access);
    this.el(".access-text").style.opacity = String(s.accessOpacity);
    this.el(".boot-logo").style.opacity = String(s.logoOpacity);
    this.el(".boot-logo").style.transform = `translate(${s.logo.offsetX}px, 1px)`;
    this.contour.style.strokeDasharray = `${s.logo.length} ${1 - s.logo.length}`;
    this.contour.style.strokeDashoffset = String(-s.logo.start);
    this.contour.setAttribute("stroke-width", String(s.logo.strokeWidth));
    if (this.letters.textContent !== s.logoLetters) this.letters.textContent = s.logoLetters;
    this.plus.style.opacity = this.minus.style.opacity = s.logo.symbolScale > 0 ? "1" : "0";
    this.plus.setAttribute(
      "transform",
      `translate(${s.logo.plusX} 70) rotate(${s.logo.plusAngle}) scale(${s.logo.symbolScale}) translate(-69 -70)`,
    );
    this.minus.setAttribute("d", `M${-s.logo.minusWidth / 2} 0h${s.logo.minusWidth}`);
    this.minus.setAttribute(
      "transform",
      `translate(${s.logo.minusX} 70) scale(${s.logo.symbolScale})`,
    );
    this.el(".auth-status").style.opacity = String(s.authOpacity);
    this.applyAuth(s.auth, s.f);
    this.brandLines.forEach((node, i) => {
      node.style.opacity = String(s.brand[i].opacity);
      node.style.transform = `translateX(${s.brand[i].x}px)`;
    });
    this.el(".powered").style.opacity = String(Number(s.poweredLetters > 0));
    this.el(".powered").style.clipPath = `inset(0 ${100 * (1 - s.poweredLetters / 19)}% 0 0)`;
    this.el(".scan").style.opacity = String(Number(s.scanVisible));
    if (s.scanVisible) this.renderScan(s);
    this.el(".welcome").style.opacity = String(s.welcomeVisible ? s.welcomeOpacity : 0);
    this.el(".welcome").style.transform = `scale(${s.welcomeScale})`;
    this.el(".welcome").style.filter =
      `blur(${s.exitBlur}px) invert(${s.exit * 0.22}) sepia(${s.exit}) saturate(${1 + s.exit * 5}) hue-rotate(${s.exit * 115}deg)`;
    this.el(".welcome-panel").style.opacity = String(s.welcomePanel);
    this.el(".welcome-heading").style.color =
      s.welcomeInk >= 1
        ? "var(--ink)"
        : `rgb(${255 * (1 - s.welcomeInk)} ${255 * (1 - s.welcomeInk)} ${255 * (1 - s.welcomeInk)})`;
    this.el(".welcome-company").style.opacity = String(
      s.companyVisible ? (s.companyMask ? 0.65 : 1) : 0,
    );
    this.company[1].host.style.opacity = s.companyMask ? ".06" : "1";
    this.el(".welcome-highlight").style.clipPath = `inset(0 ${100 * (1 - s.highlight)}% 0 0)`;
    this.el(".welcome-database").style.opacity = String(s.databaseOpacity);
    this.el(".welcome-logo").style.opacity = String(Number(s.welcomeLogo));
    this.el("#bootBackground").style.opacity = String(s.backgroundOpacity);
    this.el(".boot-white").style.opacity = String(s.white);
    this.el("#bootBackground svg").style.transform =
      `translate(${Math.sin(t * 0.16) * 18}px, ${-(t - 6) * 5}px) scale(1.08)`;
    return s;
  }
  renderScan(s) {
    const { scan } = s,
      r = scan.radius;
    const group = this.scanPaths[0].parentElement;
    group.setAttribute(
      "transform",
      `translate(960 540) scale(${s.ringScale}) translate(-960 -540)`,
    );
    group.style.opacity = String(s.ringOpacity);
    group.style.filter = `blur(${s.ringBlur}px)`;
    this.scanPaths[0].setAttribute("d", arc(r, scan.outerStart, scan.outerSweep));
    this.scanPaths[1].setAttribute(
      "d",
      arc(scan.whiteRadius, scan.whiteStart, scan.whiteSweep),
    );
    this.scanPaths[2].setAttribute("d", arc(scan.innerRadius, scan.innerStart, scan.innerSweep));
    this.scanPaths[3].setAttribute(
      "d",
      arc(scan.innerRadius, scan.innerStart + Math.PI, scan.innerSweep),
    );
    s.scanOrbit.sides.forEach((side, i) => {
      this.scanPaths[i + 4].setAttribute("d", arc(side.radius, side.start, side.sweep, side.x, side.y));
      this.scanPaths[i + 4].style.opacity = s.scanOrbit.sideVisible ? "1" : "0";
    });
    s.scanOrbit.satellites.forEach((point, i) => {
      const dot = this.satellites[i];
      dot.setAttribute("cx", String(point.x));
      dot.setAttribute("cy", String(point.y));
      dot.setAttribute("r", String(Math.max(0, point.radius)));
    });
    this.core.style.opacity = s.ornament ? "1" : "0";
    this.core.setAttribute("r", String(s.coreRadius));
    const orbit = scan.orbit;
    this.orbitDots.forEach((dot, i) => {
      dot.setAttribute("cx", String(960 + Math.cos(orbit + i * Math.PI) * scan.orbitRadius));
      dot.setAttribute("cy", String(540 + Math.sin(orbit + i * Math.PI) * scan.orbitRadius));
      dot.setAttribute("r", String(scan.dotRadius));
    });
    this.caps.forEach((cap, i) => {
      const angle = i ? scan.whiteStart : scan.outerStart + scan.outerSweep;
      const radius = i ? scan.whiteRadius : r;
      cap.setAttribute("cx", String(960 + Math.cos(angle) * radius));
      cap.setAttribute("cy", String(540 + Math.sin(angle) * radius));
      cap.setAttribute("r", String(i ? scan.whiteCap : scan.blackCap));
    });
    this.el(".scan-label").style.opacity = String(s.permissionOpacity);
    this.el(".scan-label").style.letterSpacing = `${s.scanTracking}px`;
    this.el(".scan-label").style.setProperty("--boot-phrase-tracking", `${s.scanTracking}px`);
    this.el(".scan-label").style.fontSize = `${s.scanFont}px`;
  }
  play() {
    if (this.playing || this.done) return;
    this.playing = true;
    this.start = performance.now();
    this.root.classList.remove("boot-done");
    const step = (now) => {
      if (!this.playing) return;
      const elapsed = (now - this.start) / 1000;
      this.update(elapsed);
      if (elapsed >= 21.92) {
        this.finish();
        return;
      }
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }
  finish() {
    if (this.done) return;
    this.done = true;
    this.playing = false;
    cancelAnimationFrame(this.frame);
    removeEventListener("keydown", this.onKey);
    this.root.classList.add("boot-done");
    this.root.hidden = false;
    setTimeout(() => {
      this.root.hidden = true;
      this.onDone?.();
    }, 520);
  }
  replay() {
    this.done = false;
    this.playing = false;
    this.root.hidden = false;
    this.build();
    addEventListener("keydown", this.onKey);
    this.play();
  }
  get active() {
    return this.playing;
  }
}
